import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const Navbar = ({ title }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, logout } = useAuth();
  const [showRoleSwitcher, setShowRoleSwitcher] = React.useState(false);
  const [switching, setSwitching] = React.useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const isSuperAdmin = user?.email === 'kayhankarli@gmail.com';

  const switchRole = async (newRole) => {
    setSwitching(true);
    try {
      await axios.post(
        `${API}/auth/switch-role`,
        { new_role: newRole },
        { withCredentials: true }
      );
      
      // Reload to apply new role
      window.location.reload();
    } catch (error) {
      console.error('Role switch error:', error);
      alert('Role değiştirilemedi');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <nav className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Home */}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            data-testid="navbar-home-button"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="text-xl font-bold hidden sm:block">{title || t('dashboard_title')}</span>
          </button>

          {/* Right Side - User Info and Actions */}
          <div className="flex items-center space-x-4">
            {/* Super Admin Role Switcher */}
            {isSuperAdmin && (
              <div className="relative">
                <button
                  onClick={() => setShowRoleSwitcher(!showRoleSwitcher)}
                  className="flex items-center space-x-2 bg-yellow-500/90 hover:bg-yellow-600 px-3 py-2 rounded-lg transition-colors"
                  title="Super Admin - Switch Role"
                >
                  <span className="text-sm">👑</span>
                  <span className="text-xs font-bold">{user?.role?.toUpperCase()}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showRoleSwitcher && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                    <div className="p-2">
                      <div className="text-xs font-semibold text-gray-500 px-3 py-1">
                        {language === 'tr' ? 'Rol Değiştir' : 'Switch Role'}
                      </div>
                      {['admin', 'teacher', 'student', 'parent'].map((role) => (
                        <button
                          key={role}
                          onClick={() => {
                            switchRole(role);
                            setShowRoleSwitcher(false);
                          }}
                          disabled={switching || user?.role === role}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            user?.role === role
                              ? 'bg-blue-100 text-blue-800 font-semibold'
                              : 'hover:bg-gray-100 text-gray-700'
                          } ${switching ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {role === 'admin' && '👑 Admin'}
                          {role === 'teacher' && '👨‍🏫 Teacher'}
                          {role === 'student' && '🎓 Student'}
                          {role === 'parent' && '👨‍👩‍👧 Parent'}
                          {user?.role === role && ' ✓'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mentor Application Button */}
            <button
              onClick={() => navigate('/mentor/apply')}
              className="hidden md:flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
              data-testid="mentor-apply-button"
            >
              <span className="text-lg">👨‍🏫</span>
              <span className="text-sm">{language === 'tr' ? 'Mentör Ol' : 'Become a Mentor'}</span>
            </button>

            {/* User Info */}
            {user && (
              <div className="flex items-center space-x-3">
                {user.picture && (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-10 h-10 rounded-full border-2 border-white/50 hidden sm:block" 
                  />
                )}
                <div className="hidden lg:block">
                  <p className="text-sm font-semibold">{user.name}</p>
                  <p className="text-xs text-white/70">{user.role}</p>
                </div>
              </div>
            )}

            {/* Logout Button */}
            <button
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="flex items-center space-x-2 bg-red-500/80 hover:bg-red-600 px-4 py-2 rounded-lg transition-colors"
              data-testid="navbar-logout-button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden sm:inline">{language === 'tr' ? 'Çıkış' : 'Logout'}</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;